import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  Play,
  CheckCircle,
  XCircle,
  LogOut,
  Users,
  Volume2,
  VolumeX,
  ArrowRight,
  LayoutDashboard,
  Trophy,
  Flame
} from "lucide-react";
import ShowedUpFeed from "../components/ShowedUpFeed";
import QuitConfirmation from "../components/QuitConfirmation";
import CardRequiredModal from "../components/CardRequiredModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_3e64acab-2561-42e6-b860-06b1a563df4d/artifacts/5nvw3yv2_6ECA6BBA-F429-4238-A743-EE6BF2D1D33F.png";

export default function QuitterMode() {
  const navigate = useNavigate();
  const { user, logout, fetchUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [todayStatus, setTodayStatus] = useState(null); // null, 'pending', 'completed', 'missed'
  const [showCheckIn, setShowCheckIn] = useState(false);
  const [showMissedReason, setShowMissedReason] = useState(false);
  const [missedReason, setMissedReason] = useState("");
  const [showQuitModal, setShowQuitModal] = useState(false);
  const [showVictory, setShowVictory] = useState(false);
  const [voiceNote, setVoiceNote] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [streak, setStreak] = useState(0);
  const [streakBroken, setStreakBroken] = useState(false);
  const [streakMessage, setStreakMessage] = useState("");
  const [showCardRequired, setShowCardRequired] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    checkSubscriptionStatus();
    fetchTodayStatus();
    fetchVoiceNote();
  }, []);

  const checkSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`);
      if (response.data.requires_payment_method) {
        setShowCardRequired(true);
      }
    } catch (error) {
      console.error("Failed to check subscription:", error);
    }
  };

  const fetchTodayStatus = async () => {
    try {
      const response = await axios.get(`${API}/quitter-mode/status`);
      setTodayStatus(response.data.status);
      setStreak(response.data.streak);
      setStreakBroken(response.data.streak_broken);
      if (response.data.streak_broken) {
        setStreakMessage(response.data.streak_message);
      }
    } catch (error) {
      console.error("Failed to fetch status:", error);
      setTodayStatus('pending');
    } finally {
      setLoading(false);
    }
  };

  const fetchVoiceNote = async () => {
    try {
      const response = await axios.get(`${API}/quitter-mode/voice-note`);
      setVoiceNote(response.data);
    } catch (error) {
      console.error("Failed to fetch voice note:", error);
    }
  };

  const playVoiceNote = async () => {
    if (!voiceNote?.audio_url && voiceNote?.text) {
      // Generate TTS
      try {
        const response = await axios.post(`${API}/voice/generate`, {
          text: voiceNote.text,
          voice: "nova",
          speed: 1.1
        });
        if (response.data.audio_url) {
          setVoiceNote(prev => ({ ...prev, audio_url: response.data.audio_url }));
          playAudio(response.data.audio_url);
        }
      } catch (error) {
        toast.error("Couldn't play voice note");
      }
    } else if (voiceNote?.audio_url) {
      playAudio(voiceNote.audio_url);
    }
  };

  const playAudio = (url) => {
    if (audioRef.current) {
      audioRef.current.src = url;
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleCheckIn = async (showedUp) => {
    if (showedUp) {
      // Navigate to workout
      navigate(`/workout/${user?.current_day || 1}`);
    } else {
      setShowMissedReason(true);
    }
  };

  const submitMissedReason = async () => {
    try {
      await axios.post(`${API}/quitter-mode/checkin`, {
        showed_up: false,
        reason: missedReason
      });
      setTodayStatus('missed');
      setShowMissedReason(false);
      setShowCheckIn(false);
      // Refresh status to get streak info
      fetchTodayStatus();
      toast("Tomorrow is still open.", { icon: "💪" });
    } catch (error) {
      toast.error("Failed to save check-in");
    }
  };

  const handleWorkoutComplete = async () => {
    try {
      await axios.post(`${API}/quitter-mode/checkin`, {
        showed_up: true
      });
      setTodayStatus('completed');
      setShowVictory(true);
      await fetchUser();
      fetchTodayStatus();
    } catch (error) {
      console.error("Failed to record completion:", error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-primary animate-pulse text-2xl font-bold tracking-widest">LOADING...</div>
      </div>
    );
  }

  // MANDATORY CARD CHECK - Block all access until card is added
  if (showCardRequired) {
    return (
      <div className="min-h-screen bg-black">
        <CardRequiredModal />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      {/* Hidden audio element */}
      <audio 
        ref={audioRef} 
        onEnded={() => setIsPlaying(false)} 
        className="hidden" 
      />

      {/* Streak Broken Message */}
      <AnimatePresence>
        {streakBroken && streakMessage && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className="fixed top-0 left-0 right-0 z-50 bg-zinc-900 border-b border-zinc-800 p-4"
          >
            <div className="max-w-xl mx-auto text-center">
              <p className="text-zinc-400 text-sm">{streakMessage}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Victory Overlay */}
      <AnimatePresence>
        {showVictory && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          >
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", duration: 0.6 }}
              className="text-center p-8"
            >
              <motion.div
                animate={{ 
                  scale: [1, 1.2, 1],
                  rotate: [0, 10, -10, 0]
                }}
                transition={{ duration: 0.5, repeat: 2 }}
              >
                <CheckCircle className="h-32 w-32 text-green-500 mx-auto mb-6" />
              </motion.div>
              <h2 className="text-4xl font-black uppercase tracking-tighter mb-4">
                YOU SHOWED UP
              </h2>
              <p className="text-zinc-400 text-lg mb-2">
                Day {user?.current_day || 1} complete.
              </p>
              <div className="flex items-center justify-center gap-2 text-primary mb-8">
                <Flame className="h-6 w-6" />
                <span className="text-2xl font-bold">{streak + 1} day streak</span>
              </div>
              <Button
                onClick={() => setShowVictory(false)}
                className="bg-white text-black hover:bg-zinc-200 uppercase font-bold tracking-widest px-8 py-4"
              >
                Continue
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Quit Confirmation Modal */}
      <QuitConfirmation 
        open={showQuitModal} 
        onClose={() => setShowQuitModal(false)} 
      />

      {/* Missed Reason Modal */}
      <AnimatePresence>
        {showMissedReason && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-md"
            >
              <Card className="bg-zinc-900 border-zinc-800 p-6">
                <h3 className="text-xl font-bold uppercase tracking-tight mb-4">
                  What stopped you?
                </h3>
                <div className="space-y-3 mb-6">
                  {[
                    "No time",
                    "Too tired", 
                    "Forgot",
                    "Not feeling it",
                    "Life happened"
                  ].map((reason) => (
                    <Button
                      key={reason}
                      variant={missedReason === reason ? "default" : "outline"}
                      className={`w-full justify-start ${
                        missedReason === reason 
                          ? "bg-primary text-white" 
                          : "border-zinc-700 text-zinc-400 hover:text-white"
                      }`}
                      onClick={() => setMissedReason(reason)}
                    >
                      {reason}
                    </Button>
                  ))}
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setShowMissedReason(false);
                      setMissedReason("");
                    }}
                    className="flex-1 text-zinc-500"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={submitMissedReason}
                    disabled={!missedReason}
                    className="flex-1 bg-white text-black hover:bg-zinc-200"
                  >
                    Submit
                  </Button>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-black/80 backdrop-blur-lg border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Coach Coop" className="h-8 w-8 rounded-sm" />
              <span className="font-bold text-sm tracking-tight">QUITTER MODE</span>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="text-zinc-500 hover:text-white">
                  <LayoutDashboard className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">Dashboard</span>
                </Button>
              </Link>
              <Button variant="ghost" size="icon" onClick={handleLogout} className="text-zinc-500 hover:text-white">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-20 pb-32 px-4 min-h-screen flex flex-col">
        <div className="max-w-xl mx-auto w-full flex-1 flex flex-col">
          
          {/* Streak Display */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="flex items-center justify-center gap-2 text-zinc-500">
              <Flame className={`h-5 w-5 ${streak > 0 ? 'text-primary' : ''}`} />
              <span className="text-sm uppercase tracking-wider">
                {streak > 0 ? `${streak} day streak` : 'Start your streak'}
              </span>
            </div>
          </motion.div>

          {/* Main Card - The Only Thing They See */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="flex-1 flex flex-col justify-center"
          >
            {todayStatus === 'completed' ? (
              // Already completed today
              <Card className="bg-zinc-900 border-zinc-800 p-8 text-center">
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <h2 className="text-2xl font-black uppercase tracking-tighter mb-2">
                  YOU SHOWED UP TODAY
                </h2>
                <p className="text-zinc-500 mb-6">Come back tomorrow.</p>
                <div className="flex items-center justify-center gap-2 text-primary">
                  <Flame className="h-5 w-5" />
                  <span className="font-bold">{streak} day streak</span>
                </div>
              </Card>
            ) : todayStatus === 'missed' ? (
              // Missed today
              <Card className="bg-zinc-900 border-zinc-800 p-8 text-center">
                <XCircle className="h-16 w-16 text-zinc-600 mx-auto mb-4" />
                <h2 className="text-xl font-bold uppercase tracking-tighter mb-2 text-zinc-400">
                  You didn't fail.
                </h2>
                <p className="text-zinc-500 mb-4">
                  You chose not to show up. Tomorrow is still open.
                </p>
                <p className="text-xs text-zinc-600">Check back tomorrow</p>
              </Card>
            ) : (
              // Pending - Main focus
              <Card className="bg-zinc-900 border-zinc-800 p-8">
                <div className="text-center">
                  <p className="text-zinc-500 text-sm uppercase tracking-widest mb-4">
                    Today's task
                  </p>
                  <h1 className="text-4xl sm:text-5xl font-black uppercase tracking-tighter mb-2">
                    20 MINUTES
                  </h1>
                  <p className="text-zinc-400 mb-8">Day {user?.current_day || 1} of 60</p>
                  
                  <Button
                    data-testid="start-workout-btn"
                    size="lg"
                    onClick={() => navigate(`/workout/${user?.current_day || 1}`)}
                    className="w-full bg-primary hover:bg-primary/90 text-white uppercase font-bold tracking-widest py-6 text-lg mb-4"
                  >
                    <Play className="mr-2 h-5 w-5" />
                    START NOW
                  </Button>

                  {/* Voice Note Button */}
                  {voiceNote && (
                    <Button
                      variant="ghost"
                      onClick={playVoiceNote}
                      disabled={isPlaying}
                      className="text-zinc-500 hover:text-white w-full"
                    >
                      {isPlaying ? (
                        <>
                          <Volume2 className="mr-2 h-4 w-4 animate-pulse" />
                          Playing...
                        </>
                      ) : (
                        <>
                          <Volume2 className="mr-2 h-4 w-4" />
                          Listen to Coach
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </Card>
            )}
          </motion.div>

          {/* Daily Check-In */}
          {todayStatus === 'pending' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-8"
            >
              <p className="text-center text-zinc-500 text-sm uppercase tracking-wider mb-4">
                End of day check-in
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  data-testid="checkin-yes-btn"
                  variant="outline"
                  onClick={() => handleCheckIn(true)}
                  className="border-green-500/30 text-green-500 hover:bg-green-500/10 hover:text-green-400 py-6"
                >
                  <CheckCircle className="mr-2 h-5 w-5" />
                  I showed up
                </Button>
                <Button
                  data-testid="checkin-no-btn"
                  variant="outline"
                  onClick={() => handleCheckIn(false)}
                  className="border-zinc-700 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-400 py-6"
                >
                  <XCircle className="mr-2 h-5 w-5" />
                  I didn't
                </Button>
              </div>
            </motion.div>
          )}

          {/* Showed Up Feed */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-8"
          >
            <ShowedUpFeed />
          </motion.div>
        </div>
      </main>

      {/* Bottom Bar - Quit Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-black/80 backdrop-blur-lg border-t border-zinc-800 p-4">
        <div className="max-w-xl mx-auto flex justify-center">
          <Button
            data-testid="quit-btn"
            variant="ghost"
            onClick={() => setShowQuitModal(true)}
            className="text-zinc-600 hover:text-red-500 text-sm uppercase tracking-wider"
          >
            I Quit
          </Button>
        </div>
      </div>
    </div>
  );
}
